import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
  Divider,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";

import BasicModal from "./BasicModal";
import SelectStatus from "../SelectStatus";
import { PrimaryButton } from "../";
import { RadioForm } from "../RadioForm";

import PlanoAcao from "../../types/negocio/PlanoAcao";
import MetaGlobal from "../../types/negocio/MetaGlobal";
import Colaborador from "../../types/negocio/Colaborador";
import {
  CONFRONTO,
  DEFENSIVA,
  OFENSIVA,
  REFORCO,
} from "../../types/constants/estrategias";
import {
  AMEACA_NORMALIZADO,
  OPORTUNIDADE_NORMALIZADO,
} from "../../types/constants/ClassificacaoAmbienteExterno";
import { ambienteInternoPontoFracoService } from "../../services/AmbienteInternoPontoFracoService";
import { ambienteInternoPontoForteService } from "../../services/AmbienteInternoPontoForteService";
import { ambienteExternoService } from "../../services/AmbienteExternoService";
import { planoAcaoService } from "../../services/PlanoAcaoService";
import { AmbienteInternoPontoForte } from "../../types/negocio/AmbienteInternoPontoForte";
import { AmbienteInternoPontoFraco } from "../../types/negocio/AmbienteInternoPontoFraco";
import { AmbienteExterno } from "../../types/negocio/AmbienteExterno";
import { normalize } from "../../utils/string";
import toastService from "../../utils/toastService";

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (data: PlanoAcao) => void;
  plano: PlanoAcao;
  setPlano: React.Dispatch<React.SetStateAction<PlanoAcao>>;
  metasGlobais: MetaGlobal[];
  colaboradores: Colaborador[];
  modoEdicao?: boolean;
  planosExistentes?: any[];
};

export default function PlanoAcaoModal({
  open,
  onClose,
  onSave,
  plano,
  setPlano,
  metasGlobais,
  colaboradores,
  modoEdicao = true,
  planosExistentes = [],
}: Props) {
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [pontosFortes, setPontosFortes] = useState<AmbienteInternoPontoForte[]>(
    []
  );
  const [oportunidades, setOportunidades] = useState<AmbienteExterno[]>([]);
  const [pontosFracos, setPontosFracos] = useState<AmbienteInternoPontoFraco[]>(
    []
  );
  const [ameacas, setAmeacas] = useState<AmbienteExterno[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingLoading, setSavingLoading] = useState(false);

  // carregar dados adicionais para cruzamento
  useEffect(() => {
    Promise.all([
      ambienteExternoService.listarTodos(),
      ambienteInternoPontoFracoService.listarTodos(),
      ambienteInternoPontoForteService.listarTodos(),
    ])
      .then(([externos, fracos, fortes]) => {
        setAmeacas(
          externos.filter(
            (item) =>
              normalize(item.fator) === AMEACA_NORMALIZADO && item.pontuacao > 0
          )
        );
        setOportunidades(
          externos.filter(
            (item) =>
              normalize(item.fator) === OPORTUNIDADE_NORMALIZADO &&
              item.pontuacao > 0
          )
        );
        setPontosFracos(fracos.filter((item) => item.pontuacao > 0));
        setPontosFortes(fortes.filter((item) => item.pontuacao > 0));
        setLoading(false);
      })
      .catch(() => {
        toastService.error("Erro ao carregar dados de ambiente.");
        setLoading(false);
      });
  }, []);

  // controlar steps
  useEffect(() => {
    setCompletedSteps((prev) => [!!plano.fatorExternoGuid, ...prev.slice(1)]);
  }, [plano.fatorExternoGuid]);
  useEffect(() => {
    setCompletedSteps((prev) => [
      prev[0],
      !!plano.fatorInternoGuid,
      prev[2],
      prev[3],
    ]);
  }, [plano.fatorInternoGuid]);
  useEffect(() => {
    setCompletedSteps((prev) => [
      prev[0],
      prev[1],
      !!plano.metaGlobalGuid,
      prev[3],
    ]);
  }, [plano.metaGlobalGuid]);
  useEffect(() => {
    const preenchido =
      plano.acao &&
      plano.responsavelGuid &&
      plano.prazoConclusao &&
      plano.status &&
      plano.area;
    setCompletedSteps((prev) => [prev[0], prev[1], prev[2], !!preenchido]);
  }, [
    plano.acao,
    plano.responsavelGuid,
    plano.prazoConclusao,
    plano.status,
    plano.area,
  ]);

  const definirEstrategia = () => {
    const fatorExterno =
      oportunidades.find((o) => o.guid === plano.fatorExternoGuid) ||
      ameacas.find((a) => a.guid === plano.fatorExternoGuid);
    const fatorInterno =
      pontosFortes.find((pf) => pf.guid === plano.fatorInternoGuid) ||
      pontosFracos.find((pf) => pf.guid === plano.fatorInternoGuid);

    if (fatorExterno && fatorInterno) {
      const isOportunidade = oportunidades.some((o) => o.guid === fatorExterno.guid);
      const isAmeaca = ameacas.some((a) => a.guid === fatorExterno.guid);
      const isPontoForte = pontosFortes.some((pf) => pf.guid === fatorInterno.guid);
      const isPontoFraco = pontosFracos.some((pf) => pf.guid === fatorInterno.guid);

      if (isOportunidade && isPontoForte) plano.estrategia = OFENSIVA;
      else if (isOportunidade && isPontoFraco) plano.estrategia = CONFRONTO;
      else if (isAmeaca && isPontoForte) plano.estrategia = REFORCO;
      else if (isAmeaca && isPontoFraco) plano.estrategia = DEFENSIVA;
    }
  };

  const salvarPlano = async () => {
    setSavingLoading(true);
    definirEstrategia();

    try {
      // Verifica se o plano já existe na lista (edição) ou se é novo (criação)
      const planoJaExiste = planosExistentes.some((p) => p.guid === plano.guid);
      
      if (planoJaExiste) {
        await planoAcaoService.atualizar(plano);
        toastService.success("Plano atualizado com sucesso!");
      } else {
        await planoAcaoService.criar(plano);
        toastService.success("Plano criado com sucesso!");
      }
      onSave(plano);
      onClose();
    } catch (err) {
      toastService.error("Erro ao salvar plano de ação.");
    } finally {
      setSavingLoading(false);
    }
  };

  return (
    <BasicModal
      title={modoEdicao ? "Plano de Ação" : "Novo Plano de Ação"}
      open={open}
      onClose={onClose}
      saveButton
      onSave={salvarPlano}
    >
      {loading ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ padding: 2, maxWidth: 800, margin: "auto" }}>
          {/* Campos */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Fator Externo */}
            <FormControl fullWidth color="secondary">
              <InputLabel>Fator Externo</InputLabel>
              <Select
                value={plano.fatorExternoGuid}
                onChange={(e) =>
                  setPlano((prev) => ({
                    ...prev,
                    fatorExternoGuid: e.target.value,
                  }))
                }
              >
                {[...oportunidades, ...ameacas].map((item) => (
                  <MenuItem key={item.guid} value={item.guid}>
                    {item.opcao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Linha com 2 colunas: Fator Interno + Meta Global */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth color="secondary">
                <InputLabel>Fator Interno</InputLabel>
                <Select
                  value={plano.fatorInternoGuid}
                  onChange={(e) =>
                    setPlano((prev) => ({
                      ...prev,
                      fatorInternoGuid: e.target.value,
                    }))
                  }
                >
                  {[...pontosFortes, ...pontosFracos].map((item) => (
                    <MenuItem key={item.guid} value={item.guid}>
                      {item.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth color="secondary">
                <InputLabel>Meta Global</InputLabel>
                <Select
                  value={plano.metaGlobalGuid}
                  onChange={(e) =>
                    setPlano((prev) => ({
                      ...prev,
                      metaGlobalGuid: e.target.value,
                      area:
                        metasGlobais.find((m) => m.guid === e.target.value)?.area ||
                        "",
                    }))
                  }
                >
                  {metasGlobais.map((meta) => (
                    <MenuItem key={meta.guid} value={meta.guid}>
                      {meta.area}: {meta.meta}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Ação */}
            <TextField
              fullWidth
              label="Ação"
              variant="outlined"
              color="secondary"
              value={plano.acao}
              onChange={(e) =>
                setPlano((prev) => ({ ...prev, acao: e.target.value }))
              }
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth color="secondary">
                <InputLabel>Responsável</InputLabel>
                <Select
                  value={plano.responsavelGuid}
                  onChange={(e) =>
                    setPlano((prev) => ({
                      ...prev,
                      responsavelGuid: e.target.value,
                    }))
                  }
                >
                  {colaboradores.map((c) => (
                    <MenuItem key={c.guid} value={c.guid}>
                      {c.nomeCompleto}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={ptBR}
              >
                <DatePicker
                  label="Prazo"
                  value={plano.prazoConclusao ? new Date(plano.prazoConclusao) : null}
                  onChange={(newDate) =>
                    setPlano((prev) => ({
                      ...prev,
                      prazoConclusao: newDate ?? new Date(),
                    }))
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      color: "secondary",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <SelectStatus
              value={plano.status}
              onChange={(value) =>
                setPlano((prev) => ({ ...prev, status: value }))
              }
            />
          </Box>
        </Box>
      )}
    </BasicModal>
  );
}